import type { RadarData, RadarConfig, Entry, RadarEntry, ChangeInfo } from '@/types/radar';
import { MarkdownParser } from './markdownParser';

/**
 * Loads and processes radar data from markdown files
 */
export class DataLoader {
  private parser: MarkdownParser;
  private cache: RadarData | null = null;

  constructor() {
    this.parser = new MarkdownParser();
  }

  /**
   * Load all radar data
   */
  async load(): Promise<RadarData> {
    if (this.cache) {
      return this.cache;
    }

    const config = await this.loadConfig();
    const entries = await this.loadEntries();
    const entriesWithChanges = this.applyChangeDetection(entries);

    // Find the most recent git timestamp across all entries
    const mostRecentTimestamp = entries.reduce((latest, entry) => {
      if (entry.gitTimestamp && entry.gitTimestamp > latest) {
        return entry.gitTimestamp;
      }
      return latest;
    }, new Date(0));

    // Use most recent git timestamp, or current date if no git metadata available
    const lastUpdated = mostRecentTimestamp.getTime() > 0 ? mostRecentTimestamp : new Date();

    const data: RadarData = {
      config,
      entries: entriesWithChanges,
      lastUpdated,
    };

    this.cache = data;
    return data;
  }

  /**
   * Load configuration
   */
  private async loadConfig(): Promise<RadarConfig> {
    // Default configuration
    return {
      title: 'Tech Radar',
      organization: 'Your Organization',
      description: 'Our technology choices and strategic direction',
      quadrants: [
        {
          id: 'languages-frameworks',
          name: 'Languages & Frameworks',
          order: 0,
          description: 'Programming languages and frameworks we use',
        },
        {
          id: 'tools',
          name: 'Tools',
          order: 1,
          description: 'Development and operational tools',
        },
        {
          id: 'platforms',
          name: 'Platforms',
          order: 2,
          description: 'Infrastructure and platform services',
        },
        {
          id: 'techniques',
          name: 'Techniques',
          order: 3,
          description: 'Development practices and methodologies',
        },
      ],
      rings: [
        {
          id: 'adopt',
          name: 'Adopt',
          description: 'Technologies we have high confidence in and actively use',
          order: 0,
          color: '#5ba300',
        },
        {
          id: 'trial',
          name: 'Trial',
          description: 'Technologies worth pursuing to understand their impact',
          order: 1,
          color: '#009eb0',
        },
        {
          id: 'assess',
          name: 'Assess',
          description: 'Technologies to explore and understand their potential',
          order: 2,
          color: '#c7ba00',
        },
        {
          id: 'hold',
          name: 'Hold',
          description: 'Technologies to proceed with caution or avoid',
          order: 3,
          color: '#e09b96',
        },
      ],
    };
  }

  /**
   * Load entries from markdown files
   */
  private async loadEntries(): Promise<Entry[]> {
    // In browser environment, we need to fetch the manifest
    try {
      const response = await fetch('/radar/manifest.json');
      if (!response.ok) {
        throw new Error('Failed to load manifest');
      }

      interface ManifestEntry {
        path: string;
        git?: { hash: string; author: string; timestamp: string } | null;
        ringHistory?: { previousRing: string; movedAt: string } | null;
      }

      const manifest: ManifestEntry[] = await response.json();

      const files = await Promise.all(
        manifest.map(async (entry) => {
          const path = typeof entry === 'string' ? entry : entry.path;
          const gitMetadata = typeof entry === 'object' ? entry.git : null;
          const ringHistory = typeof entry === 'object' ? entry.ringHistory : null;

          const response = await fetch(`/${path}`);
          const content = await response.text();
          return { path, content, git: gitMetadata, ringHistory };
        })
      );

      const entries = await this.parser.parseEntries(files);

      // Add git metadata and ring history to entries
      return entries.map((entry, index) => {
        const fileData = files[index];
        const result: Entry = { ...entry };

        if (fileData.git) {
          result.gitHash = fileData.git.hash;
          result.gitAuthor = fileData.git.author;
          result.gitTimestamp = new Date(fileData.git.timestamp);
        }

        if (fileData.ringHistory) {
          result.previousRing = fileData.ringHistory.previousRing as any;
          result.ringMovedAt = new Date(fileData.ringHistory.movedAt);
        }

        return result;
      });
    } catch (error) {
      console.error('Error loading entries:', error);
      return [];
    }
  }

  /**
   * Apply change detection to entries
   * This is a placeholder - actual implementation will use git history
   */
  private applyChangeDetection(entries: Entry[]): RadarEntry[] {
    return entries.map((entry) => ({
      ...entry,
      change: this.detectChange(entry),
    }));
  }

  /**
   * Detect change status for an entry
   * Uses git timestamp if available, otherwise falls back to lastModified
   * Detects ring movements by comparing current ring with historical data
   */
  private detectChange(entry: Entry): ChangeInfo {
    // Check if ring has moved in the last 30 days
    if (entry.previousRing && entry.ringMovedAt) {
      const daysSinceMove = (Date.now() - entry.ringMovedAt.getTime()) / (1000 * 60 * 60 * 24);

      if (daysSinceMove < 30) {
        // Determine movement direction based on ring order
        const ringOrder: { [key: string]: number } = {
          'adopt': 0,
          'trial': 1,
          'assess': 2,
          'hold': 3,
        };

        const currentOrder = ringOrder[entry.ring] ?? 0;
        const previousOrder = ringOrder[entry.previousRing] ?? 0;

        const direction: 'inward' | 'outward' =
          currentOrder < previousOrder ? 'inward' : 'outward';

        return {
          status: 'moved',
          previousRing: entry.previousRing,
          direction,
          movedAt: entry.ringMovedAt,
        };
      }
    }

    // Check if entry is new (created in last 30 days)
    const timestamp = entry.gitTimestamp || entry.lastModified;
    const daysSinceModified =
      (Date.now() - timestamp.getTime()) / (1000 * 60 * 60 * 24);

    if (daysSinceModified < 30) {
      return { status: 'new' };
    }

    return { status: 'unchanged' };
  }

  /**
   * Clear cache to force reload
   */
  clearCache(): void {
    this.cache = null;
  }

  /**
   * Filter entries by criteria
   */
  filterEntries(
    entries: RadarEntry[],
    filters: {
      quadrant?: string;
      ring?: string;
      tags?: string[];
      search?: string;
    }
  ): RadarEntry[] {
    return entries.filter((entry) => {
      if (filters.quadrant && entry.quadrant !== filters.quadrant) {
        return false;
      }

      if (filters.ring && entry.ring !== filters.ring) {
        return false;
      }

      if (filters.tags && filters.tags.length > 0) {
        const hasTag = filters.tags.some((tag) => entry.tags.includes(tag));
        if (!hasTag) return false;
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesTitle = entry.title.toLowerCase().includes(searchLower);
        const matchesDescription = entry.description.toLowerCase().includes(searchLower);
        const matchesTags = entry.tags.some((tag) => tag.toLowerCase().includes(searchLower));

        if (!matchesTitle && !matchesDescription && !matchesTags) {
          return false;
        }
      }

      return true;
    });
  }
}
